import { Toast, type ToastProps } from '@src/components/modals/Toast';
import { type PropsWithChildren, useCallback, useMemo, useState } from 'react';
import { v4 as uuid } from 'uuid';
import { ToastContext, type ToastContextType } from './useToastContext';

export function ToastProvider({ children }: PropsWithChildren) {
  const [toasts, setToasts] = useState<(ToastProps & { id: string })[]>([]);

  const addToast = useCallback((toast: ToastProps) => {
    const id = uuid();
    setToasts((t) => [...t, { id, ...toast }]);
    return id;
  }, []);

  const removeToast = useCallback((id: string) => {
    setToasts((t) => t.filter((toast) => toast.id !== id));
  }, []);

  const removeAllToasts = useCallback(() => {
    setToasts([]);
  }, []);

  const api: ToastContextType = useMemo(
    () => ({
      add: addToast,
      remove: removeToast,
      removeAll: removeAllToasts,
    }),
    [addToast, removeAllToasts, removeToast],
  );

  return (
    <ToastContext.Provider value={api}>
      {children}
      <div className="fixed ml-3 sm:ml-0 top-3 right-3 flex flex-col gap-3 z-[var(--z-index-toast)]">
        {toasts.map((t) => (
          <Toast
            key={t.id}
            {...t}
            onClose={() => {
              t.onClose?.();
              removeToast(t.id);
            }}
            data-testid={`${t.type}-toast`}
            closable={true}
          />
        ))}
      </div>
    </ToastContext.Provider>
  );
}
